import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type {
	AiDanceVideoDanceId,
	AiDanceVideoQualityId,
} from "./ai-dance-video-preset";

/**
 * /ai-dance-video-generator 样片区的片子；工作台右栏按当前选中的舞放对应那条（自己传视频时放第一条）。
 *
 * 全是本页工作台 HD（Kling 2.6 motion-control 720p）按舞蹈库出的自产片，每支舞一条，onSite true，标档位，
 * 「Try this example」切到那支舞并回填原图。来源和压制参数记在 packages/scripts/ai-dance-video-showcase/README.md。
 *
 * R2 `home/showcase/ai-dance-video/` 下 clips/{id}-720p.mp4、posters/{id}-720p.jpg、refs/{ref}.jpg；
 * 右栏和样片区用同一份 720p 文件。清单为空时样片区整个不渲染。
 */
export interface AiDanceVideoShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档，拼贴 hover 时显示档位名 */
	quality: AiDanceVideoQualityId;
	/** 出这条片用的舞；「Try this example」顺带切过去 */
	template: AiDanceVideoDanceId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 原图（一张全身照），「Try this example」填进工作台的槽 */
	inputImages: string[];
}

const R2_BASE = "home/showcase/ai-dance-video";

/**
 * 视频和 poster 地址的版本参数（assets 域边缘缓存一天，连 404 也缓存）；换片子时加一。
 * 从 3 起：上传前本地 dev 已经请求过 v=1、v=2，边缘缓存了 404。4：Funky、Street 换成 v2 参考舞重出（11/01）。
 * 5：Party、Disco、Bounce 也换成 v2 参考舞重出（11/01 晚）。6：Hip-Hop 也换
 */
const CLIP_VERSION = 6;

type Spec = Omit<
	AiDanceVideoShowcaseClip,
	"videoUrl" | "poster" | "inputImages"
> & {
	/** refs/ 下的原图文件名 */
	ref: string;
};

/**
 * 顺序即拼贴顺序，第一条也是自己传视频时的右栏 Example。全竖版，2026-11-01 本页 HD 出（88 积分一条），
 * 原图是 gpt-image-2 虚构人物全身照，和舞蹈库一一配对
 */
const SPECS: Spec[] = [
	{
		id: "hd-hiphop",
		title: "Rooftop Hip-Hop Dance",
		description:
			"A full-body photo of a woman with braids on a city rooftop becomes a hip-hop dance video, every move copied from the Hip-Hop dance in our library.",
		quality: "hd",
		template: "hiphop",
		orientation: "portrait",
		onSite: true,
		duration: 8,
		hasAudio: true,
		ref: "p1-hiphop",
	},
	{
		id: "hd-funky",
		title: "Office Funky Groove",
		description:
			"A photo of a man in a shirt and tie at the office becomes a funky groove dance, with his face, clothes and office kept from the photo.",
		quality: "hd",
		template: "funky",
		orientation: "portrait",
		onSite: true,
		duration: 8,
		hasAudio: true,
		ref: "p2-funky",
	},
	{
		id: "hd-disco",
		title: "Grandma Disco Dance",
		description:
			"A photo of a grandma in her vintage living room becomes a disco dance video, with music added, ready to share.",
		quality: "hd",
		template: "disco",
		orientation: "portrait",
		onSite: true,
		duration: 8,
		hasAudio: true,
		ref: "p4-disco",
	},
	{
		id: "hd-party",
		title: "Living Room Party Groove",
		description:
			"A photo of a woman in a sweater at home becomes a party groove dance, moving to the beat in her own living room.",
		quality: "hd",
		template: "party",
		orientation: "portrait",
		onSite: true,
		duration: 8,
		hasAudio: true,
		ref: "p3-party",
	},
	{
		id: "hd-street",
		title: "Basketball Court Freestyle",
		description:
			"A photo of a young man in a hoodie on a basketball court becomes a street freestyle dance video.",
		quality: "hd",
		template: "street",
		orientation: "portrait",
		onSite: true,
		duration: 8,
		hasAudio: true,
		ref: "p5-street",
	},
	{
		id: "hd-bounce",
		title: "Boardwalk Bounce Dance",
		description:
			"A photo of a woman in a yellow dress on a seaside boardwalk becomes a bouncy dance video in the sun.",
		quality: "hd",
		template: "bounce",
		orientation: "portrait",
		onSite: true,
		duration: 8,
		hasAudio: true,
		ref: "p6-bounce",
	},
];

export const aiDanceVideoShowcaseClips: AiDanceVideoShowcaseClip[] = SPECS.map(
	({ ref, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}-720p.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}-720p.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: [buildR2AssetsImageUrl(`${R2_BASE}/refs/${ref}.jpg`)],
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const AI_DANCE_VIDEO_SHOWCASE_UPLOADED_AT = "2026-11-01T00:00:00+08:00";
